import React, { useState, useEffect, useRef, useCallback } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  Bell,
  Search,
  ChevronDown,
  Menu,
  X,
  Sparkles,
  LogIn,
  LogOut,
  Users,
  DoorOpen,
  Ticket,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { residents as residentsApi, visitors as visitorsApi, helpdesk, notifications as notificationsApi, type Resident, type Visitor, type Ticket as ApiTicket, type AppNotification } from "../services/api";

const NexoraLogo: React.FC<{ size?: number }> = ({ size = 32 }) => (
  <div
    style={{
      width: size,
      height: size,
      background: "linear-gradient(135deg, #1A6CF6 0%, #6D28D9 100%)",
      borderRadius: size * 0.28,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flexShrink: 0,
      boxShadow: "0 4px 16px rgba(26,108,246,0.4)",
    }}
  >
    <span
      style={{
        fontFamily: "Syne, sans-serif",
        fontWeight: 800,
        fontSize: size * 0.42,
        color: "#fff",
        letterSpacing: "-0.05em",
      }}
    >
      Nx
    </span>
  </div>
);

interface NavbarProps {
  variant?: "transparent" | "solid" | "dashboard";
}

const Navbar: React.FC<NavbarProps> = ({ variant = "transparent" }) => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const isDashboard = variant === "dashboard";

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const isActive = (path: string) => location.pathname === path;

  const THEME_LS_KEY = "nexora-selected-theme";
  const themeOptions = [
    { name: "Executive Suite", path: "/dashboard/themes/executive-suite" },
    { name: "Modern Minimal", path: "/dashboard/themes/modern-minimal" },
    { name: "Corporate Edge", path: "/dashboard/themes/corporate-edge" },
  ];

  const [themeMenuOpen, setThemeMenuOpen] = useState(false);
  const [selectedTheme, setSelectedTheme] = useState<{
    name: string;
    path: string;
  } | null>(null);
  const [avatarOpen, setAvatarOpen] = useState(false);
  const avatarRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  // Notifications
  const [notifOpen, setNotifOpen] = useState(false);
  const [notifUnread, setNotifUnread] = useState(0);
  const [notifItems, setNotifItems] = useState<AppNotification[]>([]);
  const [notifLoading, setNotifLoading] = useState(false);
  const notifRef = useRef<HTMLDivElement>(null);

  const { user, logout } = useAuth();
  const initials = user?.fullName
    ? user.fullName.split(" ").slice(0, 2).map((w) => w[0].toUpperCase()).join("")
    : "?";
  const roleLabel: Record<string, string> = {
    SUPER_ADMIN: "Super Admin",
    SOCIETY_ADMIN: "Admin",
    STAFF: "Staff",
    RESIDENT: "Resident",
    VIEWER: "Viewer",
  };

  // Search
  const [searchQuery, setSearchQuery] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchResults, setSearchResults] = useState<{ type: string; label: string; sub: string; path: string }[]>([]);
  const searchRef = useRef<HTMLDivElement>(null);

  const runSearch = useCallback(async (q: string) => {
    const sid = user?.societyId;
    if (!q.trim() || !sid) { setSearchResults([]); return; }
    const lower = q.toLowerCase();
    const results: { type: string; label: string; sub: string; path: string }[] = [];
    try {
      const [rPage, vPage, tPage] = await Promise.allSettled([
        residentsApi.list(sid),
        visitorsApi.list(sid),
        helpdesk.list(sid),
      ]);
      if (rPage.status === "fulfilled") {
        const items: Resident[] = (rPage.value as any).content ?? rPage.value;
        items.filter((r: Resident) => r.fullName?.toLowerCase().includes(lower) || r.email?.toLowerCase().includes(lower))
          .slice(0, 3).forEach((r: Resident) => results.push({ type: "resident", label: r.fullName, sub: r.email ?? "", path: "/dashboard/residents" }));
      }
      if (vPage.status === "fulfilled") {
        const items: Visitor[] = (vPage.value as any).content ?? vPage.value;
        items.filter((v: Visitor) => v.name?.toLowerCase().includes(lower) || v.purpose?.toLowerCase().includes(lower))
          .slice(0, 3).forEach((v: Visitor) => results.push({ type: "visitor", label: v.name, sub: v.purpose ?? v.status, path: "/dashboard/visitors" }));
      }
      if (tPage.status === "fulfilled") {
        const items: ApiTicket[] = (tPage.value as any).content ?? tPage.value;
        items.filter((t: ApiTicket) => t.title?.toLowerCase().includes(lower) || t.category?.toLowerCase().includes(lower))
          .slice(0, 3).forEach((t: ApiTicket) => results.push({ type: "ticket", label: t.title, sub: t.status, path: "/dashboard/helpdesk" }));
      }
    } catch { /* ignore */ }
    setSearchResults(results);
  }, [user?.societyId]);

  useEffect(() => {
    const timer = setTimeout(() => runSearch(searchQuery), 300);
    return () => clearTimeout(timer);
  }, [searchQuery, runSearch]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) {
        setSearchOpen(false);
      }
    };
    if (searchOpen) document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [searchOpen]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (avatarRef.current && !avatarRef.current.contains(e.target as Node)) {
        setAvatarOpen(false);
      }
    };
    if (avatarOpen) document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [avatarOpen]);

  useEffect(() => {
    if (!user) return;
    notificationsApi.unreadCount().then((r) => setNotifUnread(r.count)).catch(() => {});
    const timer = setInterval(() => {
      notificationsApi.unreadCount().then((r) => setNotifUnread(r.count)).catch(() => {});
    }, 30000);
    return () => clearInterval(timer);
  }, [user]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (notifRef.current && !notifRef.current.contains(e.target as Node)) {
        setNotifOpen(false);
      }
    };
    if (notifOpen) document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [notifOpen]);

  const toggleNotifications = () => {
    const next = !notifOpen;
    setNotifOpen(next);
    if (next) {
      setNotifLoading(true);
      notificationsApi.list(0, 10)
        .then((page) => setNotifItems(page.content))
        .catch(() => {})
        .finally(() => setNotifLoading(false));
    }
  };

  const handleNotifClick = (n: AppNotification) => {
    if (n.read) return;
    setNotifItems((prev) => prev.map((x) => (x.id === n.id ? { ...x, read: true } : x)));
    setNotifUnread((c) => Math.max(0, c - 1));
    notificationsApi.markRead(n.id).catch(() => {});
  };

  useEffect(() => {
    try {
      const stored = window.localStorage.getItem(THEME_LS_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (parsed?.name && parsed?.path) {
          setSelectedTheme(parsed);
        }
      }
    } catch {
      // ignore malformed storage value
    }
  }, []);

  const saveThemeSelection = (theme: { name: string; path: string }) => {
    setSelectedTheme(theme);
    window.localStorage.setItem(THEME_LS_KEY, JSON.stringify(theme));
  };

  /* ─── Dashboard Topbar ─── */
  if (isDashboard) {
    return (
      <header
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          zIndex: 100,
          height: 60,
          background: "var(--nx-topbar-bg)",
          borderBottom: "1px solid var(--nx-topbar-border)",
          display: "flex",
          alignItems: "center",
          padding: "0 24px",
          gap: 16,
        }}
      >
        {/* Logo */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            width: 220,
            flexShrink: 0,
          }}
        >
          <NexoraLogo size={32} />
          <div>
            <div
              style={{
                fontFamily: "Syne, sans-serif",
                fontWeight: 800,
                fontSize: 16,
                color: "var(--nx-topbar-text)",
                letterSpacing: "-0.04em",
              }}
            >
              NEXORA
            </div>
            <div
              style={{
                fontSize: 9,
                color: "rgba(255,255,255,0.35)",
                letterSpacing: "0.1em",
              }}
            >
              BY PENTAGON ANALYTICS
            </div>
          </div>
        </div>

        {/* Current society */}
        {user?.societyName && (
          <div
            title={user.societyCode ? `Society code: ${user.societyCode}` : undefined}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              padding: "6px 14px",
              borderRadius: 999,
              background: "rgba(26,108,246,0.12)",
              border: "1px solid rgba(26,108,246,0.25)",
              flexShrink: 0,
              maxWidth: 260,
            }}
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#60A5FA" strokeWidth="2" style={{ flexShrink: 0 }}>
              <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
              <polyline points="9 22 9 12 15 12 15 22" />
            </svg>
            <span
              style={{
                fontSize: 13,
                fontWeight: 700,
                color: "var(--nx-topbar-text)",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {user.societyName}
            </span>
            {user.societyCode && (
              <span
                style={{
                  fontSize: 10,
                  fontWeight: 600,
                  color: "#60A5FA",
                  background: "rgba(96,165,250,0.15)",
                  padding: "2px 6px",
                  borderRadius: 6,
                  flexShrink: 0,
                }}
              >
                {user.societyCode}
              </span>
            )}
          </div>
        )}

        {/* Search */}
        <div ref={searchRef} style={{ flex: 1, maxWidth: 380, position: "relative" }}>
          <Search
            size={14}
            style={{
              position: "absolute",
              left: 12,
              top: "50%",
              transform: "translateY(-50%)",
              color: "var(--nx-topbar-text-subtle)",
              pointerEvents: "none",
            }}
          />
          <input
            value={searchQuery}
            onChange={e => { setSearchQuery(e.target.value); setSearchOpen(true); }}
            onFocus={() => setSearchOpen(true)}
            placeholder="Search residents, visitors, tickets..."
            style={{
              width: "100%",
              padding: "8px 36px 8px 34px",
              background: "var(--nx-topbar-input-bg)",
              border: "1px solid var(--nx-topbar-input-border)",
              borderRadius: 8,
              color: "var(--nx-topbar-text)",
              fontSize: 13,
              fontFamily: "var(--font-body)",
              outline: "none",
              boxSizing: "border-box",
            }}
          />
          {searchQuery ? (
            <button onClick={() => { setSearchQuery(""); setSearchResults([]); }}
              style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: "var(--nx-topbar-text-subtle)", display: "flex", padding: 2 }}>
              <X size={12} />
            </button>
          ) : (
            <span style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", fontSize: 10, color: "var(--nx-topbar-text-subtle)", background: "var(--nx-topbar-input-bg)", padding: "2px 6px", borderRadius: 4, pointerEvents: "none" }}>
              ⌘K
            </span>
          )}

          {/* Search dropdown */}
          {searchOpen && searchQuery.trim() && (
            <div style={{ position: "absolute", top: "calc(100% + 6px)", left: 0, right: 0, background: "rgba(8,14,29,0.98)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 12, boxShadow: "0 16px 40px rgba(0,0,0,0.4)", zIndex: 300, overflow: "hidden" }}>
              {searchResults.length === 0 ? (
                <div style={{ padding: "16px", fontSize: 12, color: "rgba(255,255,255,0.35)", textAlign: "center" }}>No results for "{searchQuery}"</div>
              ) : searchResults.map((r, i) => (
                <button key={i} onClick={() => { navigate(r.path); setSearchOpen(false); setSearchQuery(""); }}
                  style={{ width: "100%", display: "flex", alignItems: "center", gap: 10, padding: "10px 14px", background: "transparent", border: "none", cursor: "pointer", textAlign: "left" }}
                  onMouseEnter={e => (e.currentTarget.style.background = "rgba(255,255,255,0.05)")}
                  onMouseLeave={e => (e.currentTarget.style.background = "transparent")}
                >
                  <div style={{ width: 28, height: 28, borderRadius: 7, background: r.type === "resident" ? "rgba(26,108,246,0.15)" : r.type === "visitor" ? "rgba(109,40,217,0.15)" : "rgba(245,158,11,0.15)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, color: r.type === "resident" ? "#60A5FA" : r.type === "visitor" ? "#A78BFA" : "#FCD34D" }}>
                    {r.type === "resident" ? <Users size={13} /> : r.type === "visitor" ? <DoorOpen size={13} /> : <Ticket size={13} />}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 12, fontWeight: 500, color: "#fff", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.label}</div>
                    <div style={{ fontSize: 10, color: "rgba(255,255,255,0.4)", textTransform: "capitalize" }}>{r.type} · {r.sub}</div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        <div
          style={{
            marginLeft: "auto",
            display: "flex",
            alignItems: "center",
            gap: 8,
          }}
        >
          <div style={{ position: "relative" }}>
            <button
              type="button"
              onClick={() => setThemeMenuOpen((open) => !open)}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                padding: "8px 14px",
                borderRadius: 999,
                border: "1px solid var(--nx-topbar-button-border)",
                background: "var(--nx-topbar-button-bg)",
                color: "var(--nx-topbar-text)",
                fontSize: 12,
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              <span>Theme: {selectedTheme?.name || "Select"}</span>
              <ChevronDown size={14} />
            </button>

            {themeMenuOpen && (
              <div
                style={{
                  position: "absolute",
                  top: "calc(100% + 8px)",
                  right: 0,
                  width: 220,
                  background: "rgba(8,14,29,0.98)",
                  border: "1px solid rgba(255,255,255,0.08)",
                  borderRadius: 16,
                  boxShadow: "0 18px 48px rgba(15,23,42,0.28)",
                  zIndex: 200,
                  overflow: "hidden",
                }}
                onMouseLeave={() => setThemeMenuOpen(false)}
              >
                {themeOptions.map((theme) => (
                  <Link
                    key={theme.path}
                    to={theme.path}
                    onClick={() => {
                      saveThemeSelection(theme);
                      setThemeMenuOpen(false);
                    }}
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      width: "100%",
                      padding: "12px 16px",
                      background: "transparent",
                      color: "#fff",
                      textDecoration: "none",
                      fontSize: 13,
                    }}
                  >
                    <span>{theme.name}</span>
                    <ChevronDown
                      size={14}
                      style={{ transform: "rotate(90deg)" }}
                    />
                  </Link>
                ))}
              </div>
            )}
          </div>

          {/* AI Badge */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 5,
              padding: "5px 12px",
              background: "rgba(26,108,246,0.12)",
              border: "1px solid rgba(26,108,246,0.2)",
              borderRadius: 99,
              fontSize: 11,
              color: "#60A5FA",
              fontWeight: 600,
            }}
          >
            <Sparkles size={12} />
            NexaBot Active
          </div>

          {/* Notifications */}
          <div ref={notifRef} style={{ position: "relative" }}>
            <button
              onClick={toggleNotifications}
              style={{
                width: 36,
                height: 36,
                borderRadius: 9,
                background: "var(--nx-topbar-button-bg)",
                border: "1px solid var(--nx-topbar-input-border)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                position: "relative",
              }}
            >
              <Bell size={16} color="var(--nx-topbar-icon-color)" />
              {notifUnread > 0 && (
                <span
                  style={{
                    position: "absolute",
                    top: 5,
                    right: 5,
                    minWidth: 14,
                    height: 14,
                    padding: "0 3px",
                    borderRadius: 99,
                    background: "var(--nx-red)",
                    border: "1.5px solid var(--nx-topbar-bg)",
                    fontSize: 9,
                    fontWeight: 700,
                    color: "#fff",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {notifUnread > 9 ? "9+" : notifUnread}
                </span>
              )}
            </button>

            {notifOpen && (
              <div
                style={{
                  position: "absolute",
                  top: "calc(100% + 8px)",
                  right: 0,
                  width: 320,
                  maxHeight: 420,
                  overflowY: "auto",
                  background: "rgba(8,14,29,0.98)",
                  border: "1px solid rgba(255,255,255,0.08)",
                  borderRadius: 12,
                  boxShadow: "0 18px 48px rgba(0,0,0,0.4)",
                  zIndex: 300,
                }}
              >
                <div style={{ padding: "12px 16px", borderBottom: "1px solid rgba(255,255,255,0.08)", fontSize: 12, fontWeight: 700, color: "#fff" }}>
                  Notifications
                </div>
                {notifLoading ? (
                  <div style={{ padding: 20, textAlign: "center", fontSize: 12, color: "rgba(255,255,255,0.5)" }}>Loading…</div>
                ) : notifItems.length === 0 ? (
                  <div style={{ padding: 20, textAlign: "center", fontSize: 12, color: "rgba(255,255,255,0.5)" }}>No notifications yet</div>
                ) : (
                  notifItems.map((n) => (
                    <button
                      key={n.id}
                      onClick={() => handleNotifClick(n)}
                      style={{
                        display: "block",
                        width: "100%",
                        textAlign: "left",
                        padding: "12px 16px",
                        background: "none",
                        border: "none",
                        borderBottom: "1px solid rgba(255,255,255,0.05)",
                        cursor: "pointer",
                        opacity: n.read ? 0.55 : 1,
                      }}
                    >
                      <div style={{ fontSize: 12, fontWeight: 600, color: "#fff" }}>{n.title}</div>
                      {!!n.body && <div style={{ fontSize: 11, color: "rgba(255,255,255,0.6)", marginTop: 2 }}>{n.body}</div>}
                      <div style={{ fontSize: 10, color: "rgba(255,255,255,0.35)", marginTop: 4 }}>
                        {new Date(n.createdAt).toLocaleString(undefined, { day: "numeric", month: "short", hour: "numeric", minute: "2-digit" })}
                      </div>
                    </button>
                  ))
                )}
              </div>
            )}
          </div>

          {/* Role badge — always visible, not just inside the avatar dropdown */}
          {user?.role && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                padding: "5px 12px",
                background: "rgba(26,108,246,0.12)",
                border: "1px solid rgba(26,108,246,0.2)",
                borderRadius: 99,
                fontSize: 11,
                fontWeight: 700,
                color: "#60A5FA",
                flexShrink: 0,
              }}
            >
              {roleLabel[user.role] ?? user.role}
            </div>
          )}

          {/* Avatar + Sign-out dropdown */}
          <div ref={avatarRef} style={{ position: "relative" }}>
            <button
              onClick={() => setAvatarOpen((o) => !o)}
              title={user?.fullName ?? "Account"}
              style={{
                width: 36,
                height: 36,
                borderRadius: "50%",
                background: "linear-gradient(135deg, #1A6CF6, #6D28D9)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 13,
                fontWeight: 700,
                color: "#fff",
                cursor: "pointer",
                fontFamily: "Syne, sans-serif",
                border: avatarOpen ? "2px solid #1A6CF6" : "2px solid transparent",
                outline: "none",
                transition: "border-color 0.15s",
              }}
            >
              {initials}
            </button>

            {avatarOpen && (
              <div
                style={{
                  position: "absolute",
                  top: "calc(100% + 8px)",
                  right: 0,
                  minWidth: 200,
                  background: "rgba(8,14,29,0.98)",
                  border: "1px solid rgba(255,255,255,0.08)",
                  borderRadius: 12,
                  boxShadow: "0 18px 48px rgba(0,0,0,0.4)",
                  zIndex: 300,
                  overflow: "hidden",
                }}
              >
                {/* User info */}
                <div style={{ padding: "14px 16px", borderBottom: "1px solid rgba(255,255,255,0.07)" }}>
                  <div style={{ fontWeight: 700, fontSize: 13, color: "#fff", fontFamily: "Syne, sans-serif", marginBottom: 2 }}>
                    {user?.fullName ?? "—"}
                  </div>
                  <div style={{ fontSize: 11, color: "rgba(255,255,255,0.4)", marginBottom: 6 }}>
                    {user?.email ?? ""}
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                    {user?.role && (
                      <div style={{ display: "inline-block", padding: "2px 8px", borderRadius: 99, fontSize: 10, fontWeight: 600, background: "rgba(26,108,246,0.15)", color: "#60A5FA" }}>
                        {roleLabel[user.role] ?? user.role}
                      </div>
                    )}
                    {user?.societyName && (
                      <div style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "2px 8px", borderRadius: 99, fontSize: 10, fontWeight: 500, background: "rgba(255,255,255,0.07)", color: "rgba(255,255,255,0.55)" }}>
                        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>
                        {user.societyName}
                      </div>
                    )}
                  </div>
                </div>

                {/* Sign out */}
                <button
                  onClick={() => { setAvatarOpen(false); logout(); }}
                  style={{
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    padding: "12px 16px",
                    background: "transparent",
                    border: "none",
                    color: "#F87171",
                    fontSize: 13,
                    fontWeight: 600,
                    cursor: "pointer",
                    textAlign: "left",
                  }}
                  onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = "rgba(239,68,68,0.08)"; }}
                  onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = "transparent"; }}
                >
                  <LogOut size={14} />
                  Sign Out
                </button>
              </div>
            )}
          </div>
        </div>
      </header>
    );
  }

  /* ─── Landing Navbar ─── */
  const navBg = scrolled ? "rgba(8,14,29,0.92)" : "transparent";
  const navBorder = scrolled
    ? "1px solid rgba(255,255,255,0.06)"
    : "1px solid transparent";

  const navLinks = [
    { label: "Features", href: "#features" },
    { label: "AI", href: "#ai" },
    { label: "Pricing", href: "#pricing" },
    { label: "About", href: "#about" },
  ];

  return (
    <>
      <header
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          zIndex: 100,
          padding: "0 5%",
          height: 68,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          background: navBg,
          borderBottom: navBorder,
          backdropFilter: scrolled ? "blur(20px)" : "none",
          transition: "all 400ms cubic-bezier(0.4,0,0.2,1)",
        }}
      >
        {/* Logo */}
        <Link
          to="/"
          style={{
            textDecoration: "none",
            display: "flex",
            alignItems: "center",
            gap: 10,
          }}
        >
          <NexoraLogo size={36} />
          <div>
            <div
              style={{
                fontFamily: "Syne, sans-serif",
                fontWeight: 800,
                fontSize: 18,
                color: "#fff",
                letterSpacing: "-0.04em",
                lineHeight: 1,
              }}
            >
              NEXORA
            </div>
            <div
              style={{
                fontSize: 8,
                color: "rgba(255,255,255,0.3)",
                letterSpacing: "0.12em",
              }}
            >
              BY PENTAGON ANALYTICS
            </div>
          </div>
        </Link>

        {/* Nav Links - Desktop */}
        <nav
          style={{
            display: "flex",
            alignItems: "center",
            gap: 4,
            listStyle: "none",
          }}
          className="nx-desktop-nav"
        >
          {navLinks.map((link) => (
            <a
              key={link.label}
              href={link.href}
              style={{
                padding: "8px 14px",
                color: isActive(link.href) ? "#fff" : "rgba(255,255,255,0.6)",
                fontSize: 14,
                fontWeight: 500,
                textDecoration: "none",
                borderRadius: 8,
                transition: "color 200ms, background 200ms",
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLElement).style.color = "#fff";
                (e.currentTarget as HTMLElement).style.background =
                  "rgba(255,255,255,0.06)";
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLElement).style.color =
                  "rgba(255,255,255,0.6)";
                (e.currentTarget as HTMLElement).style.background =
                  "transparent";
              }}
            >
              {link.label}
            </a>
          ))}
        </nav>

        {/* CTA */}
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <Link
            to="/login"
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              padding: "8px 14px",
              color: "rgba(255,255,255,0.6)",
              fontSize: 14,
              fontWeight: 500,
              textDecoration: "none",
              borderRadius: 8,
              transition: "color 200ms",
            }}
          >
            <LogIn size={15} />
            Sign In
          </Link>
          <Link
            to="/register"
            className="nx-btn nx-btn-primary"
            style={{ padding: "9px 20px", fontSize: 13 }}
          >
            <LayoutDashboard size={14} />
            Get Started
          </Link>
        </div>
      </header>

      {/* Mobile nav overlay */}
      {mobileOpen && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 99,
            background: "rgba(8,14,29,0.98)",
            backdropFilter: "blur(20px)",
            display: "flex",
            flexDirection: "column",
            padding: "80px 24px 24px",
            gap: 8,
          }}
        >
          {navLinks.map((link) => (
            <a
              key={link.label}
              href={link.href}
              onClick={() => setMobileOpen(false)}
              style={{
                padding: "14px 16px",
                color: "#fff",
                fontSize: 18,
                fontWeight: 600,
                textDecoration: "none",
                borderBottom: "1px solid rgba(255,255,255,0.06)",
                fontFamily: "Syne, sans-serif",
              }}
            >
              {link.label}
            </a>
          ))}
        </div>
      )}
    </>
  );
};

export default Navbar;
export { NexoraLogo };
